import { useEffect, useMemo, useRef, useState } from 'react';
import { useCart, priceCart } from '../store/cart';
import { amount, rupees } from '../lib/format';
import { methodLabel } from '../lib/types';
import { paiseToInput, parseRupeesToPaise, planSplit, type PaymentPayload, type TenderMethod } from '../lib/payments';
import { changeBreakdown, describePiece, mrpSavingsPaise, NOTE_RUPEES, upiPayUri } from '../lib/till';
import type { RoundOffMode } from '@sp/core';
import { Icon } from './icons';
import { Button, cx, Kbd, Modal } from './ui';
import { UpiQr } from './UpiQr';
import { useT } from '../i18n';

/**
 * Payment, as a sheet beside the bill it settles.
 *
 * Built so the common cases need no typing:
 *   - Cash: the amount starts at the exact bill. Tap each note the customer hands over (in the
 *     notes' own colours) and the change is shown in large type, broken into the notes and coins
 *     to give back.
 *   - UPI: when the store has a UPI ID, a QR with the amount already in it. The customer scans,
 *     the cashier types the UTR from the customer's screen, and that reference is still required:
 *     an unreferenced UPI payment cannot be reconciled.
 *   - Card / other: the amount is pre-filled with the balance, only the reference is typed.
 *   - Split: "Split payment" adds a row and pre-fills what is still to pay.
 *
 * The arithmetic (lib/payments.ts) matches what the server enforces: the `amountPaise`
 * values sum exactly to the bill, cash change travels as `tenderedPaise` on the cash row,
 * and UPI / card need a reference. Problems are shown beside the row they belong to.
 */

interface Props {
  onCustomer?: () => void;
  interState: boolean;
  roundOffMode: RoundOffMode;
  submitting: boolean;
  /** An error from the server for the last attempt, shown inside the dialog. */
  error?: string | null;
  /** The store's UPI ID and name, when configured: turns on the payment QR. */
  upi?: { vpa: string; payeeName: string } | null;
  /** Told how much is being taken by UPI, so the customer screen can show the same QR. */
  onUpiChange?: (upi: { uri: string; amountPaise: number } | null) => void;
  onConfirm: (payments: PaymentPayload[]) => void;
  onCancel: () => void;
}

interface Leg {
  id: number;
  method: TenderMethod;
  /** What the cashier typed, in rupees. Parsed to integer paise on every render. */
  text: string;
  reference: string;
  /** Notes tapped for a cash leg, in rupees, so the screen can say "₹500 + ₹200". */
  notes: number[];
}

const METHODS: TenderMethod[] = ['CASH', 'UPI', 'CARD', 'OTHER'];
const MAX_LEGS = 4;
let legSeed = 0;

export function PaymentScreen({ onCustomer, interState, roundOffMode, submitting, error, upi, onUpiChange, onConfirm, onCancel }: Props) {
  const lines = useCart((state) => state.lines);
  const billDiscount = useCart((state) => state.billDiscount);
  const customerName = useCart((state) => state.customerName);

  const taxExempt = useCart((state) => state.taxExempt);
  const t = useT();

  const { totals, taxRemovedPaise, taxReliefPaise } = useMemo(
    () => priceCart(lines, billDiscount, interState, roundOffMode, taxExempt),
    [lines, billDiscount, interState, roundOffMode, taxExempt],
  );
  const total = totals.grandTotalPaise;
  const gstPaise = totals.tax.igstPaise > 0 ? totals.tax.igstPaise : totals.tax.cgstPaise + totals.tax.sgstPaise;
  const savedPaise = mrpSavingsPaise(lines) + totals.totalDiscountPaise - taxReliefPaise;

  const [legs, setLegs] = useState<Leg[]>(() => [{ id: ++legSeed, method: 'CASH', text: paiseToInput(total), reference: '', notes: [] }]);
  const [attempted, setAttempted] = useState(false);
  const customerId = useCart(s => s.customerId);
  const quote = [customerId, total, interState].join(':');
  const lastQuote = useRef(quote);
  const [reviewed, setReviewed] = useState(true);
  const [requiresReview, setRequiresReview] = useState(false);
  useEffect(() => { if (lastQuote.current !== quote) { lastQuote.current = quote; setReviewed(false); setRequiresReview(true); } }, [quote]);

  const plan = useMemo(
    () => planSplit(total, legs.map((leg) => ({ method: leg.method, paise: parseRupeesToPaise(leg.text), reference: leg.reference }))),
    [total, legs],
  );

  const paiseOf = (leg: Leg) => parseRupeesToPaise(leg.text);
  const othersPaise = (index: number) => legs.reduce((sum, leg, position) => (position === index ? sum : sum + paiseOf(leg)), 0);

  // The QR follows the UPI amount: exact for a single UPI payment, the UPI part of a split.
  const upiPaise = legs.reduce((sum, leg) => (leg.method === 'UPI' ? sum + paiseOf(leg) : sum), 0);
  const upiUri = upi && upiPaise > 0 ? upiPayUri({ vpa: upi.vpa, payeeName: upi.payeeName, amountPaise: upiPaise, note: t('pay.upiNote', { store: upi.payeeName }) }) : null;
  useEffect(() => {
    onUpiChange?.(upiUri ? { uri: upiUri, amountPaise: upiPaise } : null);
  }, [upiUri, upiPaise, onUpiChange]);

  function update(index: number, patch: Partial<Leg>) {
    setLegs((current) => current.map((leg, position) => (position === index ? { ...leg, ...patch } : leg)));
  }

  function setMethod(index: number, method: TenderMethod) {
    const remaining = Math.max(0, total - othersPaise(index));
    update(index, { method, text: paiseToInput(remaining), reference: method === 'CASH' ? '' : (legs[index]?.reference ?? ''), notes: [] });
  }

  /** The first note replaces the pre-filled exact amount; every note after it adds to what was handed over. */
  function tapNote(index: number, rupee: number) {
    setLegs((current) =>
      current.map((leg, position) => {
        if (position !== index) return leg;
        const notes = [...leg.notes, rupee];
        return { ...leg, notes, text: paiseToInput(notes.reduce((sum, value) => sum + value * 100, 0)) };
      }),
    );
  }

  function addLeg() {
    const remaining = Math.max(0, total - legs.reduce((sum, leg) => sum + paiseOf(leg), 0));
    setLegs((current) => [...current, { id: ++legSeed, method: 'UPI', text: paiseToInput(remaining), reference: '', notes: [] }]);
  }

  function removeLeg(index: number) {
    setLegs((current) => (current.length === 1 ? current : current.filter((_, position) => position !== index)));
  }

  function confirm() {
    if (submitting || !reviewed) return;
    if (!plan.payments) {
      setAttempted(true);
      return;
    }
    onConfirm(plan.payments);
  }

  const issuesFor = (index: number, field: 'amount' | 'reference') =>
    plan.issues.filter((issue) => issue.index === index && issue.field === field);
  const billIssue = plan.issues.find((issue) => issue.index === -1);
  const covered = plan.remainingPaise === 0 && !plan.issues.some((issue) => issue.index === -1);
  const pieces = changeBreakdown(plan.changePaise);

  return (
    <Modal
      title={t('pay.title')}
      subtitle={[t('pay.subtitleLines', { count: lines.length }), customerName, legs.length > 1 ? t('pay.subtitlePayments', { count: legs.length }) : null].filter(Boolean).join(' · ')}
      onClose={onCancel}
      placement="sheet"
      testId="payment-screen"
      locked={submitting}
      closeLabel={t('pay.cancel')}
      restoreFocus="barcode"
      footer={
        <div className="space-y-2">
          <Button
            variant="success"
            size="lg"
            className="min-h-14 w-full rounded-xl text-lg short:min-h-12"
            data-testid="payment-complete"
            busy={submitting}
            disabled={!reviewed}
            onClick={confirm}
          >
            {submitting ? t('pay.saving') : t('pay.completeAmount', { amount: amount(total) })}
            {submitting ? null : <Kbd>Enter</Kbd>}
          </Button>
          <p className="text-center text-xs text-slate-500 short:hidden">
            <Kbd>Enter</Kbd> {t('pay.keysHint')} · <Kbd>Esc</Kbd> {t('pay.escHint')}
          </p>
        </div>
      }
    >
      <div
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !(event.target instanceof HTMLButtonElement)) {
            event.preventDefault();
            confirm();
          }
        }}
      >
        {onCustomer ? <button type="button" className="btn mb-3 min-h-11 w-full justify-start whitespace-normal short:mb-2 short:min-h-9" data-testid="payment-customer" disabled={submitting} onClick={onCustomer}><Icon name="user" size={16} />{customerName ? `${customerName} · ${t('paymentCustomer')}` : t('cart.addCustomer')}</button> : null}
        {requiresReview ? <div className="mb-3 rounded-xl bg-amber-50 p-3"><p>{t('totalsChanged')}</p><label className="flex min-h-11 items-center gap-2"><input type="checkbox" checked={reviewed} onChange={e => setReviewed(e.target.checked)} />{t('confirmTotal')}</label></div> : null}
        <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-1">
          <div>
            <p className="text-[11px] font-bold tracking-[0.12em] text-slate-500 uppercase">{t('pay.amountDue')}</p>
            <p className="tabular text-5xl leading-none font-extrabold tracking-tight text-slate-900 short:text-4xl" data-testid="payment-total">
              ₹{amount(total)}
            </p>
          </div>
          <div className="pb-1 text-right text-sm text-slate-500">
            <p className="tabular">{t('pay.inclTax', { amount: amount(gstPaise), tax: interState ? 'IGST' : 'GST' })}</p>
            {taxExempt ? (
              <p className="tabular font-semibold" data-testid="payment-tax-removed" style={{ color: 'var(--ui-warn, #b45309)' }}>
                {t('pay.noTax', { amount: amount(taxRemovedPaise) })}
              </p>
            ) : null}
            {savedPaise > 0 ? (
              <p className="font-semibold" style={{ color: 'var(--ui-success)' }}>
                {t('pay.customerSaves', { amount: amount(savedPaise) })}
              </p>
            ) : null}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2.5 short:mt-3">
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 short:py-1.5">
            <p className="text-[11px] font-bold tracking-wide text-slate-500 uppercase">{t('pay.received')}</p>
            <p className="tabular text-2xl leading-tight font-bold text-slate-900" data-testid="payment-received">
              ₹{amount(plan.tenderedPaise)}
            </p>
          </div>
          <div
            className="rounded-xl border px-4 py-2.5 short:py-1.5"
            style={
              covered
                ? { borderColor: 'var(--ui-success-solid)', background: 'var(--ui-success-soft)' }
                : { borderColor: 'var(--ui-danger-solid)', background: 'var(--ui-danger-soft)' }
            }
            role="status"
            aria-live="polite"
          >
            {plan.remainingPaise > 0 ? (
              <>
                <p className="text-[11px] font-bold tracking-wide uppercase" style={{ color: 'var(--ui-danger)' }}>
                  {t('pay.remaining')}
                </p>
                <p className="tabular text-2xl leading-tight font-bold" style={{ color: 'var(--ui-danger)' }} data-testid="payment-remaining">
                  ₹{amount(plan.remainingPaise)}
                </p>
              </>
            ) : (
              <>
                <p className="text-[11px] font-bold tracking-wide uppercase" style={{ color: 'var(--ui-success)' }}>
                  {plan.changePaise > 0 ? t('pay.giveBack') : t('pay.fullyPaid')}
                </p>
                <p className="tabular text-2xl leading-tight font-bold" style={{ color: 'var(--ui-success)' }} data-testid="payment-change">
                  ₹{amount(plan.changePaise)}
                </p>
              </>
            )}
          </div>
        </div>
        {pieces.length > 0 ? (
          <p className="mt-2 flex flex-wrap items-center gap-1.5 text-sm text-slate-600" data-testid="payment-change-breakdown">
            <span className="font-semibold">{t('pay.handBack')}</span>
            {pieces.map((piece) => (
              <span key={`${piece.kind}-${piece.paise}`} className="badge">
                {describePiece(piece)}
              </span>
            ))}
          </p>
        ) : null}

        <ul className="mt-4 space-y-3 short:mt-3" aria-label={t('pay.payments')}>
          {legs.map((leg, index) => {
            const amountIssues = issuesFor(index, 'amount');
            const referenceIssues = issuesFor(index, 'reference');
            const exactPaise = Math.max(0, total - othersPaise(index));
            const legPaise = paiseOf(leg);
            const qr = leg.method === 'UPI' && upi && legPaise > 0 ? upiPayUri({ vpa: upi.vpa, payeeName: upi.payeeName, amountPaise: legPaise, note: t('pay.upiNote', { store: upi.payeeName }) }) : null;
            return (
              <li key={leg.id} className="rounded-2xl border border-slate-200 bg-white p-3" data-testid="payment-leg">
                {legs.length > 1 ? (
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-slate-700">{t('pay.payment', { number: index + 1 })}</span>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      disabled={submitting}
                      aria-label={t('pay.removePayment', { number: index + 1 })}
                      onClick={() => removeLeg(index)}
                    >
                      {t('common.remove')}
                    </button>
                  </div>
                ) : null}
                <div role="group" aria-label={t('pay.methodFor', { number: index + 1 })} className="grid grid-cols-4 gap-1.5" data-testid="payment-method">
                  {METHODS.map((method) => (
                    <button
                      key={method}
                      type="button"
                      disabled={submitting}
                      className={cx(
                        'flex min-h-12 items-center justify-center rounded-xl border text-[15px] font-bold transition-colors',
                        leg.method === method ? 'border-transparent' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
                      )}
                      style={leg.method === method ? { background: 'var(--ui-primary)', color: 'var(--ui-primary-ink)' } : undefined}
                      aria-pressed={leg.method === method}
                      data-method={method}
                      onClick={() => setMethod(index, method)}
                    >
                      {methodLabel(method)}
                    </button>
                  ))}
                </div>

                {qr ? (
                  <div className="mt-3 flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <UpiQr value={qr} size={168} label={t('pay.upiQr', { amount: amount(legPaise) })} />
                    <div className="min-w-48 flex-1 space-y-2">
                      <p className="text-base font-bold text-slate-900">{t('pay.scanAsk')}</p>
                      <p className="text-sm text-slate-600">
                        {t('pay.scanHint', { amount: amount(legPaise) })}
                      </p>
                      <p className="text-xs text-slate-500">
                        {t('pay.pays')} <span className="font-mono font-semibold text-slate-700">{upi?.vpa}</span>
                      </p>
                    </div>
                  </div>
                ) : leg.method === 'UPI' && !upi ? (
                  <p className="mt-2 text-xs text-slate-500">{t('pay.noUpi')}</p>
                ) : null}

                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label htmlFor={`pay-amount-${leg.id}`} className="field-label">
                      {leg.method === 'CASH' ? t('pay.cashReceived') : t('pay.methodAmount', { method: methodLabel(leg.method) })}
                    </label>
                    <input
                      id={`pay-amount-${leg.id}`}
                      className="tabular field h-12 text-right text-xl font-semibold"
                      value={leg.text}
                      inputMode="decimal"
                      disabled={submitting}
                      data-testid="payment-tendered"
                      data-autofocus={index === 0 ? '' : undefined}
                      aria-invalid={attempted && amountIssues.length > 0 ? true : undefined}
                      onFocus={(event) => event.currentTarget.select()}
                      onChange={(event) => update(index, { text: event.target.value.replace(/[^\d.]/g, ''), notes: [] })}
                    />
                    {attempted
                      ? amountIssues.map((issue) => (
                          <p key={issue.message} className="field-error" role="alert">
                            {issue.message}
                          </p>
                        ))
                      : null}
                  </div>

                  {leg.method !== 'CASH' ? (
                    <div>
                      <label htmlFor={`pay-ref-${leg.id}`} className="field-label">
                        {leg.method === 'UPI' ? t('pay.upiRef') : leg.method === 'CARD' ? t('pay.cardRef') : t('pay.otherRef')}
                        {leg.method === 'UPI' || leg.method === 'CARD' ? <span style={{ color: 'var(--ui-danger)' }}> *</span> : null}
                      </label>
                      <input
                        id={`pay-ref-${leg.id}`}
                        className="field h-12 font-mono tracking-wide"
                        value={leg.reference}
                        disabled={submitting}
                        data-testid="payment-reference"
                        autoComplete="off"
                        placeholder={leg.method === 'UPI' ? t('pay.upiRefPlaceholder') : undefined}
                        aria-invalid={attempted && referenceIssues.length > 0 ? true : undefined}
                        onChange={(event) => update(index, { reference: event.target.value })}
                      />
                      {(attempted || leg.reference !== '') && referenceIssues.length > 0
                        ? referenceIssues.map((issue) => (
                            <p key={issue.message} className="field-error" role="alert">
                              {issue.message}
                            </p>
                          ))
                        : null}
                    </div>
                  ) : (
                    <div className="flex flex-col justify-end">
                      <p className="field-label">{leg.notes.length > 0 ? t('pay.notes', { notes: `₹${leg.notes.join(' + ₹')}` }) : t('pay.tapNotes')}</p>
                      <div className="flex gap-1.5">
                        <button
                          type="button"
                          className="btn h-12 flex-1"
                          disabled={submitting}
                          data-testid="payment-quick-exact"
                          onClick={() => update(index, { text: paiseToInput(exactPaise), notes: [] })}
                        >
                          {t('pay.exact')}
                        </button>
                        {leg.notes.length > 0 ? (
                          <button type="button" className="btn btn-ghost h-12" disabled={submitting} onClick={() => update(index, { text: '', notes: [] })}>
                            {t('pay.clear')}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>

                {leg.method === 'CASH' ? (
                  <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-6" role="group" aria-label={t('pay.notesGroup')}>
                    {[...NOTE_RUPEES].reverse().map((rupee) => {
                      const count = leg.notes.filter((value) => value === rupee).length;
                      return (
                        <button
                          key={rupee}
                          type="button"
                          className={`note-btn note-${rupee} tabular`}
                          disabled={submitting}
                          data-testid={`payment-quick-${rupee}`}
                          data-count={count > 0 ? count : undefined}
                          aria-label={count > 0 ? t('pay.addNoteCount', { rupee, count }) : t('pay.addNote', { rupee })}
                          onClick={() => tapNote(index, rupee)}
                        >
                          ₹{rupee}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>

        {legs.length < MAX_LEGS ? (
          <Button className="mt-3" disabled={submitting} onClick={addLeg} data-testid="payment-split">
            <Icon name="plus" size={16} />
            {t('pay.split')}
          </Button>
        ) : null}

        {attempted && billIssue ? (
          <p className="banner banner-danger mt-3" role="alert">
            {billIssue.message}
            {plan.remainingPaise > 0 ? ` ${t('pay.stillToCollect', { amount: rupees(plan.remainingPaise) })}` : ''}
          </p>
        ) : null}
        {error ? (
          <p className="banner banner-danger mt-3" role="alert" data-testid="payment-error">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
